// Shared "POST/PATCH/DELETE → throw on failure → parse JSON on success"
// helper used by every Pinia store with mutation actions
// (`useWikiPagesStore`, `useLintNotesStore`). Each store previously
// re-implemented the same try/catch shape — `if (!res.ok) throw new
// Error(body.error || 'verb failed')` — and a local `buildError`. The
// helper concentrates that into one place so the stores' action bodies
// shrink to a single `apiMutate` call.
//
// Not a Vue composable (no reactive state, no lifecycle), so it lives
// alongside `extractSnippet` rather than under a `use*` name. The
// `composables/` directory is the conventional home for store-side
// helpers in this repo.
//
// Usage:
//
//   await apiMutate<WikiPageDetail>(`/api/v1/wiki_pages/${id}`, {
//     method: 'PATCH',
//     body: { wiki_page: input },
//     failureLabel: 'failed to update page',
//   })
//
// On success: returns the parsed JSON body cast to `T`.
// On failure: throws `Error` with `body.error` if present, otherwise
// `<failureLabel> (<status>)`.
//
// Pass `onError` to suppress re-throwing (best-effort callers):
//
//   await apiMutate(url, { method: 'POST', failureLabel: '…', onError: () => {} })

export type ApiMutateMethod = 'POST' | 'PATCH' | 'DELETE'

export interface ApiMutateOptions {
  method: ApiMutateMethod
  // JSON-serialisable body. Omit for DELETE / "no-body POST" actions
  // (e.g. `/api/v1/lint_notes/:id/accept`); the helper only adds the
  // Content-Type header when a body is supplied.
  body?: Record<string, unknown>
  // Human-readable label baked into the fallback error message when
  // the server response has no `error` field. Phrase it as a verbed
  // failure clause: e.g. `"failed to update page"` or `"accept failed"`.
  failureLabel: string
  // HTTP status codes that should be treated as success even though
  // they're not 2xx. The cancel-source flow tolerates 404 because the
  // row is already terminal — the caller just wants "stopped, somehow".
  // The helper returns `null` (rather than parsing the body) for these
  // statuses, so the typical caller signature stays `Promise<T>` and
  // tolerated callers know to ignore the result.
  tolerateStatuses?: readonly number[]
  // When provided, called with the Error instead of re-throwing.
  // Returns `null` so callers can safely ignore the result.
  onError?: (e: Error) => void
}

// Overloads — narrowest first:
//   onError present → T | null
export function apiMutate<T = unknown>(
  url: string,
  options: ApiMutateOptions & { onError: (e: Error) => void },
): Promise<T | null>
//   tolerateStatuses present → T | null
export function apiMutate<T = unknown>(
  url: string,
  options: ApiMutateOptions & { tolerateStatuses: readonly number[] },
): Promise<T | null>
//   neither → T (throws on failure)
export function apiMutate<T = unknown>(url: string, options: ApiMutateOptions): Promise<T>
export async function apiMutate<T = unknown>(url: string, options: ApiMutateOptions): Promise<T | null> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (options.body !== undefined) headers['Content-Type'] = 'application/json'

  try {
    const res = await fetch(url, {
      method: options.method,
      credentials: 'include',
      headers,
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    })

    if (options.tolerateStatuses?.includes(res.status)) return null
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as { error?: string } | null
      throw new Error(body?.error || `${options.failureLabel} (${res.status})`)
    }
    // 204 No Content (DELETE revoke / destroy actions) has an empty body —
    // calling res.json() on it throws "Unexpected end of JSON input" in the
    // browser, which the caller would surface as a spurious failure even
    // though the mutation succeeded. Return null for no-content success;
    // body-returning callers get a 2xx WITH a payload and are unaffected.
    if (res.status === 204) return null
    return (await res.json()) as T
  } catch (e) {
    if (options.onError) {
      options.onError(e instanceof Error ? e : new Error(String(e)))
      return null
    }
    throw e
  }
}
